import { FullProject, Task, VacationEntry, ExternalCalendarEntry } from '../../types';

const SAMPLE_INBOX_IDS = ['inbox_1', 'inbox_2', 'inbox_3', 'inbox_4', 'inbox_5'];
const SAMPLE_PROCESSED_IDS = ['processed_1', 'processed_2', 'processed_3', 'processed_4', 'processed_5'];

/**
 * Normalizes system project IDs (`inbox_inforum`, `comm_internal`), deduplicates
 * legacy user-scoped system projects, and removes sample inbox items once a user
 * has created custom projects.
 */
export function buildClientAllProjects(
  allProjects: FullProject[],
  INBOX_PROJECT_ID: string,
  COMM_PROJECT_ID: string
): FullProject[] {
  // 1. Map IDs and normalize names for system projects (including legacy user-specific duplicates)
  const mapped = allProjects.map(p => {
    const originalId = p.id;
    if (p.id === INBOX_PROJECT_ID || p.id.toLowerCase().startsWith('inbox_inforum')) {
      return { ...p, id: 'inbox_inforum', name: 'Nicht zugeordnet', _originalId: originalId };
    }
    if (p.id === COMM_PROJECT_ID || p.id.toLowerCase().startsWith('comm_internal')) {
      return { ...p, id: 'comm_internal', name: 'Kommunikation', _originalId: originalId };
    }
    return { ...p, _originalId: originalId };
  });

  // 2. De-duplicate by ID: if multiple entries map to the same ID, merge their tasks and metadata
  const uniqueMap = new Map<string, FullProject & { _originalId?: string }>();
  mapped.forEach(p => {
    const existing = uniqueMap.get(p.id);
    if (existing) {
      const mergedTasks = [...(existing.tasks || [])];
      (p.tasks || []).forEach(t => {
        if (!mergedTasks.some(mt => mt.id === t.id)) {
          mergedTasks.push(t);
        }
      });

      const existingMeta = existing.metadata || {};
      const pMeta = p.metadata || {};

      // Merge vacations arrays
      const existingVacations = existingMeta.vacations || [];
      const pVacations = pMeta.vacations || [];
      const mergedVacations = [...existingVacations];
      pVacations.forEach((v: VacationEntry) => {
        if (v && v.id && !mergedVacations.some((mv: VacationEntry) => mv.id === v.id)) {
          mergedVacations.push(v);
        }
      });

      // Merge external calendars
      const existingCalendars = existingMeta.externalCalendars || [];
      const pCalendars = pMeta.externalCalendars || [];
      const mergedCalendars = [...existingCalendars];
      pCalendars.forEach((c: ExternalCalendarEntry) => {
        if (c && c.url && !mergedCalendars.some((mc: ExternalCalendarEntry) => mc.url === c.url)) {
          mergedCalendars.push(c);
        }
      });

      // If one of the projects is the user-scoped private inbox, prefer its user-specific fields
      const isExistingPrivate = existing._originalId === INBOX_PROJECT_ID;
      const isPPrivate = p._originalId === INBOX_PROJECT_ID;

      let inboxItems = existingMeta.inboxItems;
      let processedItems = existingMeta.processedItems;
      let trashItems = existingMeta.trashItems;

      if (isPPrivate) {
        inboxItems = pMeta.inboxItems;
        processedItems = pMeta.processedItems;
        trashItems = pMeta.trashItems;
      } else if (!isExistingPrivate && pMeta.inboxItems) {
        inboxItems = pMeta.inboxItems;
        processedItems = pMeta.processedItems;
        trashItems = pMeta.trashItems;
      }

      uniqueMap.set(p.id, {
        ...existing,
        tasks: mergedTasks.sort((a: Task, b: Task) => (a.sortOrder || 0) - (b.sortOrder || 0)),
        metadata: { 
          ...existingMeta, 
          ...pMeta,
          inboxItems: inboxItems || [],
          processedItems: processedItems || [],
          trashItems: trashItems || [],
          vacations: mergedVacations,
          externalCalendars: mergedCalendars
        }
      });
    } else {
      uniqueMap.set(p.id, p);
    }
  });

  const finalMerged = Array.from(uniqueMap.values());

  // Check if the user is new (no custom projects)
  const userProjects = finalMerged.filter(p => 
    p.id !== 'inbox_inforum' && 
    p.id !== INBOX_PROJECT_ID &&
    !p.id.startsWith('inbox_inforum_') &&
    !p.id.startsWith('comm_internal_') &&
    !p.id.startsWith('sample_') &&
    p.id !== 'comm_internal' &&
    p.id !== COMM_PROJECT_ID &&
    !p.archived
  );
  const isNewUser = userProjects.length === 0;

  if (!isNewUser) {
    return finalMerged.map(p => {
      if (p.id === 'inbox_inforum') {
        const meta = p.metadata || {};
        const rawInboxItems = meta.inboxItems || [];
        const rawProcessedItems = meta.processedItems || [];
        
        return {
          ...p,
          metadata: {
            ...meta,
            inboxItems: rawInboxItems.filter((item: { id?: string }) => item && !SAMPLE_INBOX_IDS.includes(item.id || '')),
            processedItems: rawProcessedItems.filter((item: { id?: string }) => item && !SAMPLE_PROCESSED_IDS.includes(item.id || ''))
          }
        };
      }
      return p;
    });
  }

  return finalMerged;
}
